<script setup>
/**
 * Budgets, in their own card beside the plan rather than inside it.
 *
 * They answer a different question. The plan asks *did the things I expect keep
 * happening*; a budget asks *how much has this category come to so far*. One is
 * a list of appointments, the other a set of meters filling up, and reading them
 * together made each harder to scan.
 */
import Icon from './ui/Icon.vue'
import BudgetRow from './BudgetRow.vue'
import { budgetHealth, budgetRows, viewedMonth } from '../stores'
import { openSheet } from '../stores/state'
import { monthLabel } from '../lib/dates'
import { t } from '../stores/i18n'
</script>

<template>
  <section class="card relative overflow-hidden">
    <header class="flex items-center gap-3 border-b border-line-soft px-4 py-3.5">
      <div class="min-w-0 flex-1">
        <h2 class="text-[15px] font-semibold">
          {{ t('budgets.title') }} · {{ monthLabel(viewedMonth, { short: true }) }}
        </h2>
        <p class="tnum mt-0.5 text-[12px]" :class="budgetHealth.alarming ? 'text-warn' : 'text-faint'">
          <template v-if="!budgetHealth.total">{{ t('budgets.nothing') }}</template>
          <template v-else-if="budgetHealth.alarming">
            {{ t('budgets.offTrackOf', { off: budgetHealth.alarming, total: budgetHealth.total }) }}
          </template>
          <template v-else>{{ t('budgets.onTrack', { count: budgetHealth.total }) }}</template>
        </p>
      </div>
      <button class="btn-soft !px-2.5 !py-2 sm:!px-3.5" :aria-label="t('budgets.addLabel')" @click="openSheet('budget')">
        <Icon name="plus" :size="16" /><span class="sr-only sm:not-sr-only">{{ t('budgets.add') }}</span>
      </button>
    </header>

    <BudgetRow v-for="row in budgetRows" :key="row.id" :row="row" />

    <div v-if="!budgetRows.length" class="px-6 py-10 text-center">
      <span class="mx-auto grid size-12 place-items-center rounded-2xl bg-raised text-faint">
        <Icon name="gauge" :size="22" />
      </span>
      <p class="mt-3 text-[14px] font-medium">{{ t('budgets.empty') }}</p>
      <p class="mt-1 text-[13px] text-muted">
        {{ t('budgets.emptyHint') }}
      </p>
      <div class="mt-4 flex justify-center">
        <button class="btn-primary" @click="openSheet('budget')">{{ t('budgets.add') }}</button>
      </div>
    </div>
  </section>
</template>
